import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useParams } from 'react-router'
import { useTheme } from '@material-ui/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { v4 as uuid } from 'uuid'
import moment from 'moment'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { useAuth } from 'hooks/useAuth'
import { useArquivoService } from 'hooks/useArquivoService'
import { useErrorService } from 'hooks/useErrorService'
import { removerExtensao } from 'utils/helpers'

export const useGaleria = (props) => {
  const { importado, tipoObjeto, idObjeto } = props
  const params = useParams()
  const { currentUser } = useAuth()
  const arquivoService = useArquivoService()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [selectedPhotos, setSelectedPhotos] = useState([])
  const [detailPhotoUuid, setDetailPhotoUuid] = useState(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [photos, setPhotos] = useState([])
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  const dragStartRef = useRef({ x: 0, y: 0 })

  const detailPhoto = useMemo(() => {
    return photos.find((p) => p.uuid === detailPhotoUuid) || null
  }, [photos, detailPhotoUuid])

  const [initialLoading, setInitialLoading] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [totalPhotos, setTotalPhotos] = useState(0)

  const errorService = useErrorService()
  const inputRef = useRef(null)
  const longPressRef = useRef(null)
  const previewsRef = useRef([]) // Rastreia URLs de previews locais para cleanup

  // Determinar o tipo e ID do objeto para API
  const objectType = useMemo(() => {
    return importado ? tipoObjeto : params.tipo
  }, [importado, tipoObjeto, params.tipo])

  const objectId = useMemo(() => {
    return importado ? idObjeto : params.id
  }, [importado, idObjeto, params.id])

  // Inicialização da Galeria: Busca todos os metadados de uma vez só
  useEffect(() => {
    const initializeGallery = async () => {
      try {
        setInitialLoading(true)
        const metaDadosFotosDB = await arquivoService.fetchMetaDadosFotos(
          objectType,
          objectId,
          0,
          500 // Limite alto para buscar todas as fotos da galeria de uma vez
        )

        if (!metaDadosFotosDB || metaDadosFotosDB.length === 0) {
          setPhotos([])
          setTotalPhotos(0)
          return
        }

        const initialPhotos = metaDadosFotosDB.map((fotoDB) => ({
          uuid: uuid(),
          id: fotoDB.id,
          src: fotoDB.urlThumb || fotoDB.url,
          usuarioUpload: fotoDB.usuarioUpload,
          urlFinal: fotoDB.url,
          urlThumb: fotoDB.urlThumb,
          mimetype: fotoDB.mimetype,
          nome: fotoDB.nome,
          estado: 'metadado',
          data: fotoDB.dataFoto || fotoDB.createdAt,
        }))

        setPhotos(initialPhotos)
        setTotalPhotos(initialPhotos.length)
      } catch (err) {
        if (err?.response?.status === 403) {
          setAccessDenied(true)
        } else {
          errorService.tratarErro(err)
        }
      } finally {
        setInitialLoading(false)
      }
    }

    if (objectType && objectId) {
      initializeGallery()
    }
  }, [objectType, objectId])

  // Controle de Zoom
  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.2, 5))
  }, [])

  const zoomOut = useCallback(() => {
    setZoom((prev) => {
      const nextZoom = Math.max(prev - 0.2, 0.5)
      if (nextZoom <= 1) {
        setPanPosition({ x: 0, y: 0 })
      }
      return nextZoom
    })
  }, [])

  // Seleção e cliques
  const handleCheck = useCallback((img) => {
    setSelectedPhotos((prev) =>
      prev.some((p) => p.id === img.id || p.uuid === img.uuid)
        ? prev.filter((p) => (img.id ? p.id !== img.id : p.uuid !== img.uuid))
        : [...prev, img]
    )
  }, [])

  const selectPhotoForDetail = useCallback((photo) => {
    setDetailPhotoUuid(photo.uuid)
    setIsDetailOpen(true)
  }, [])

  const closePhotoDetail = useCallback(() => {
    setZoom(1)
    setPanPosition({ x: 0, y: 0 })
    setDetailPhotoUuid(null)
    setIsDetailOpen(false)
  }, [])

  // Longpress em Mobile para seleção
  const handleTouchStart = useCallback((imagem) => {
    if (!isMobile) return
    longPressRef.current = setTimeout(() => {
      handleCheck(imagem)
    }, 400)
  }, [isMobile, handleCheck])

  const handleTouchEnd = useCallback(() => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current)
    }
  }, [])

  // Upload de arquivos
  const openFileSelector = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [])

  const handleSelectFiles = useCallback(async (event) => {
    const rawFiles = [...(event.target.files || [])]
    if (!rawFiles.length) return

    const preparedImages = rawFiles.map((file) => {
      const preview = URL.createObjectURL(file)
      previewsRef.current.push(preview)

      return {
        uuid: uuid(),
        file: file,
        usuarioUpload: {
          id: currentUser.id,
          nome: currentUser.nome,
        },
        preview: preview,
        data: new Date(),
        uploaded: false,
      }
    })

    setPhotos((prev) => [...prev, ...preparedImages])

    try {
      await uploadImagesSequentially(preparedImages)
    } catch (error) {
      console.error('Erro geral no processo de upload:', error)
    }
  }, [currentUser])

  const uploadImagesSequentially = async (imagesToUpload) => {
    const MAX_CONCURRENCY = 3
    const queue = [...imagesToUpload]

    const worker = async () => {
      while (queue.length > 0) {
        const image = queue.shift()
        if (!image) continue

        try {
          const formData = new FormData()
          formData.append('file', image.file)

          const idArquivo = await arquivoService.uploadArquivoGaleria(formData, objectType, objectId)

          setPhotos((prev) =>
            prev.map((photo) =>
              photo.uuid === image.uuid
                ? { ...photo, uploaded: true, id: idArquivo, file: null }
                : photo
            )
          )
        } catch (error) {
          console.error(`Erro ao subir imagem ${image.uuid}:`, error)
        }
      }
    }

    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENCY, imagesToUpload.length) },
      () => worker()
    )

    await Promise.all(workers)
  }

  // Cleanup de previews
  useEffect(() => {
    return () => {
      previewsRef.current.forEach((url) => {
        URL.revokeObjectURL(url)
      })
    }
  }, [])

  // Auxiliares de Download
  const ensureMimeType = useCallback((blob) => {
    if (!blob.type || blob.type === 'application/octet-stream') {
      return new Blob([blob], { type: 'image/jpeg' })
    }
    return blob
  }, [])

  const downloadBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [])

  const obtainImageBlob = useCallback(async (image) => {
    if (image.preview && image.file) {
      const blob = ensureMimeType(image.file)
      let filename = image.file.name
      const hasImageExtension = /\.(jpe?g|png|gif|webp)$/i.test(filename)
      if (!hasImageExtension) {
        let extension = '.jpg'
        if (blob.type === 'image/png') extension = '.png'
        else if (blob.type === 'image/gif') extension = '.gif'
        else if (blob.type === 'image/webp') extension = '.webp'
        filename = `${filename}${extension}`
      }
      return { blob, filename }
    }

    const src = image.urlFinal || image.src || image.preview
    const response = await fetch(src)
    let blob = await response.blob()
    blob = ensureMimeType(blob)

    let baseName = image.nome || image.title || 'imagem'
    baseName = removerExtensao(baseName)

    const uniqueSuffix = image.uuid ? `_${image.uuid}` : (image.id ? `_${image.id}` : '')
    
    let extension = '.jpg'
    if (blob.type === 'image/png') extension = '.png'
    else if (blob.type === 'image/gif') extension = '.gif'
    else if (blob.type === 'image/webp') extension = '.webp'

    const filename = `${baseName}${uniqueSuffix}${extension}`
    return { blob, filename }
  }, [ensureMimeType])

  const handleDownload = useCallback(async () => {
    if (!detailPhoto) return
    try {
      const { blob, filename } = await obtainImageBlob(detailPhoto)
      downloadBlob(blob, filename)
    } catch (error) {
      console.error('Erro ao baixar imagem:', error)
    }
  }, [detailPhoto, obtainImageBlob, downloadBlob])

  const handleMultipleDownload = useCallback(async () => {
    if (!selectedPhotos.length) return
    try {
      const zip = new JSZip()

      const sortedSelected = [...selectedPhotos].sort((a, b) => {
        const indexA = photos.findIndex((f) => (f.id && f.id === a.id) || f.uuid === a.uuid)
        const indexB = photos.findIndex((f) => (f.id && f.id === b.id) || f.uuid === b.uuid)
        return indexA - indexB
      })

      const files = await Promise.all(sortedSelected.map(obtainImageBlob))
      files.forEach(({ blob, filename }, idx) => {
        const prefix = String(idx + 1).padStart(2, '0')
        zip.file(`${prefix}_${filename}`, blob)
      })

      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, 'fotos.zip')
    } catch (error) {
      console.error('Erro ao baixar imagens em lote:', error)
    }
  }, [selectedPhotos, photos, obtainImageBlob])

  // Deleção de Fotos
  const handleDelete = useCallback(async () => {
    if (!selectedPhotos.length || deleting) return

    setDeleting(true)
    try {
      const deletePromises = selectedPhotos.map(async (photo) => {
        if (photo.id) {
          await arquivoService.deletarFotoGaleria(photo.id)
        }
      })

      await Promise.all(deletePromises)

      setPhotos((prev) =>
        prev.filter((photo) => !selectedPhotos.some((sel) => sel.id === photo.id))
      )
      setSelectedPhotos([])
      setIsDeleteModalOpen(false)
    } catch (error) {
      console.error('Erro ao excluir imagens:', error)
    } finally {
      setDeleting(false)
    }
  }, [selectedPhotos])

  // Selecionar tudo
  const handleSelectAll = useCallback(() => {
    const selectablePhotos = photos.filter((p) => p.id)
    if (selectedPhotos.length === selectablePhotos.length) {
      setSelectedPhotos([])
    } else {
      setSelectedPhotos(selectablePhotos)
    }
  }, [photos, selectedPhotos])

  const isSelected = useCallback((imagem) => {
    return selectedPhotos.some((sel) => (sel.id && sel.id === imagem.id) || sel.uuid === imagem.uuid)
  }, [selectedPhotos])

  const updatePhotoState = useCallback((imagem) => {
    setPhotos((prev) =>
      prev.map((item) => {
        if (item.uuid !== imagem.uuid) return item
        if (item.estado === 'metadado') {
          return {
            ...item,
            estado: 'thumb',
            src: item.urlFinal,
          }
        }
        if (item.estado === 'thumb') {
          return {
            ...item,
            estado: 'final',
          }
        }
        return item
      })
    )
  }, [])

  // Desabilitar exclusão caso foto não pertença ao usuário logado
  const deleteDisabled = useMemo(() => {
    return selectedPhotos.some((photo) => photo.usuarioUpload?.id !== currentUser.id)
  }, [selectedPhotos, currentUser])

  // Drag and Pan da imagem detalhada com Zoom
  const handleMouseDown = useCallback((e) => {
    if (zoom <= 1) return
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = {
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y
    }
  }, [zoom, panPosition])

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return
    e.preventDefault()
    setPanPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    })
  }, [isDragging])

  const handleMouseUpOrLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStartDrag = useCallback((e) => {
    if (zoom <= 1) return
    const touch = e.touches[0]
    setIsDragging(true)
    dragStartRef.current = {
      x: touch.clientX - panPosition.x,
      y: touch.clientY - panPosition.y
    }
  }, [zoom, panPosition])

  const handleTouchMoveDrag = useCallback((e) => {
    if (!isDragging) return
    if (zoom > 1) {
      e.preventDefault()
    }
    const touch = e.touches[0]
    setPanPosition({
      x: touch.clientX - dragStartRef.current.x,
      y: touch.clientY - dragStartRef.current.y
    })
  }, [isDragging, zoom])

  const handleTouchEndDrag = useCallback(() => {
    setIsDragging(false)
  }, [])

  return {
    photos,
    selectedPhotos,
    detailPhoto,
    isDetailOpen,
    zoom,
    initialLoading,
    accessDenied,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isMobile,
    deleting,

    inputRef,

    zoomIn,
    zoomOut,
    handleCheck,
    selectPhotoForDetail,
    closePhotoDetail,
    handleTouchStart,
    handleTouchEnd,
    openFileSelector,
    handleSelectFiles,
    handleDownload,
    handleMultipleDownload,
    handleDelete,
    handleSelectAll,
    isSelected,
    updatePhotoState,
    deleteDisabled,

    panPosition,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUpOrLeave,
    handleTouchStartDrag,
    handleTouchMoveDrag,
    handleTouchEndDrag,
  }
}
